import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coffee, Footprints, Smartphone, Ear, Heart, ShieldAlert, Check, ArrowRight } from 'lucide-react';
import { PillLink } from '@/components/ui-bits';
import { Reveal, FadeIn } from '@/components/motion';
import { OrnamentWave } from '@/components/Ornaments';
import { ROUTE_SLUGS } from '@/i18n';

const ICONS = [Coffee, Footprints, Smartphone, Ear, Heart];

export default function Help() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const services = t('help.services', { returnObjects: true });
  const steps = t('help.closingSteps', { returnObjects: true });
  const p = (key) => { const s = ROUTE_SLUGS[key][lang]; return s ? `/${lang}/${s}` : `/${lang}`; };

  return (
    <div data-testid="help-page">
      {/* Hero */}
      <section className="px-5 md:px-10 pt-16 pb-10 md:pt-24 md:pb-14">
        <div className="max-w-[1080px] mx-auto">
          <FadeIn>
            <span className="inline-block mb-5 text-sm font-semibold tracking-[0.18em] uppercase text-terracotta">{t('help.title')}</span>
          </FadeIn>
          <Reveal>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-textDark">
              {t('help.lead1')}<br />
              <span className="text-terracotta italic">{t('help.lead2')}</span>
            </h1>
          </Reveal>
          <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-4xl">
            <Reveal delay={0.08}><p className="text-xl text-textDark/75 leading-relaxed">{t('help.intro1')}</p></Reveal>
            <Reveal delay={0.14}><p className="text-xl text-textDark/75 leading-relaxed">{t('help.intro2')}</p></Reveal>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-5 md:px-10 pb-8">
        <div className="max-w-[1180px] mx-auto">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-textDark mb-10">{t('help.servicesTitle')}</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = ICONS[i];
              const wide = i === services.length - 1; // "Per le famiglie" full width + highlighted
              return (
                <Reveal key={i} delay={(i % 2) * 0.08} className={wide ? 'md:col-span-2' : ''}>
                  <div
                    data-testid={`help-service-${i}`}
                    className={`h-full rounded-3xl p-8 border ${wide ? 'bg-rosa border-terracotta/20' : 'bg-card border-terracotta/10'}`}
                  >
                    <span className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-salvia/25 text-salvia mb-5"><Icon size={26} /></span>
                    <h3 className="font-serif text-2xl md:text-3xl text-textDark mb-3">{s.t}</h3>
                    <p className="text-lg text-textDark/75 leading-relaxed">{s.d}</p>
                    {s.list && s.list.length > 0 && (
                      <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {s.list.map((item, k) => (
                          <li key={k} className="flex items-center gap-2.5 text-textDark/80">
                            <Check size={18} className="text-terracotta shrink-0" /> {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.note && <p className="mt-4 text-textDark/60 italic">{s.note}</p>}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HEALTH DISCLAIMER — very evident */}
      <section className="px-5 md:px-10 py-14 md:py-20">
        <div className="max-w-[1180px] mx-auto">
          <Reveal>
            <div data-testid="help-health" className="rounded-[2rem] bg-terracotta text-beige px-8 md:px-14 py-12 md:py-16 relative overflow-hidden">
              <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start relative z-10">
                <span className="shrink-0 w-16 h-16 rounded-2xl bg-beige/15 flex items-center justify-center"><ShieldAlert size={32} /></span>
                <div>
                  <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-5">{t('help.healthTitle')}</h2>
                  <p className="text-xl md:text-2xl text-beige/90 leading-relaxed mb-4">{t('help.health1')}</p>
                  <p className="text-lg md:text-xl text-beige/80 leading-relaxed">{t('help.health2')}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing */}
      <section className="px-5 md:px-10 pb-24">
        <div className="max-w-[860px] mx-auto text-center">
          <Reveal>
            <p className="font-serif text-2xl md:text-3xl text-textDark/85 leading-snug mb-10">{t('help.closingLead')}</p>
          </Reveal>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex items-center gap-3 justify-center">
                  <span className="w-9 h-9 rounded-full bg-salvia/25 text-salvia flex items-center justify-center font-serif">{i + 1}</span>
                  <span className="text-lg text-textDark font-medium">{step}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <h3 className="font-serif text-4xl md:text-5xl text-terracotta mb-8">{t('help.closingCta')}</h3>
            <PillLink to={p('contact')} testId="help-cta">{t('common.cta_write')} <ArrowRight size={20} /></PillLink>
          </Reveal>
          <div className="mt-14 flex justify-center text-terracotta/40"><OrnamentWave className="w-56 h-auto" /></div>
        </div>
      </section>
    </div>
  );
}
